import type { CSSProperties } from "react"
import type { FoilLayerEffect, FoilLayoutPatternType, FoilMaskShape, FoilPatternType, GrainType } from "./goondeck-foil-presets"

// Deterministic PRNG (not Math.random()) — these generated SVGs are
// computed once at module load and must render identically on server and
// client, otherwise Next.js flags a hydration mismatch.
function mulberry32(seed: number) {
  let a = seed
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function svgDataUrl(source: string): string {
  return `url("data:image/svg+xml,${encodeURIComponent(source)}")`
}

/** Low-poly triangle "crystal facet" mesh — used both as a mask (color only
 * shows through the ~22% of bright/open facets) and as a visible overlay
 * texture. Bimodal opacity on purpose: real cut facets either catch the
 * light or they don't, a smooth random spread just reads as gradient noise
 * instead of scattered glints. Blurred slightly to soften edges into
 * glints rather than crisp cutout shapes. */
export function buildFacetMesh(seed = 20260803, cols = 22, rows = 32, jitter = 0.4, openRate = 0.22, blur = 1.1): string {
  const rand = mulberry32(seed)
  const w = 300
  const h = 450
  const cellW = w / cols
  const cellH = h / rows

  const point = (col: number, row: number) => {
    const jx = (rand() - 0.5) * 2 * jitter * cellW
    const jy = (rand() - 0.5) * 2 * jitter * cellH
    return [col * cellW + jx, row * cellH + jy]
  }

  const grid: [number, number][][] = []
  for (let r = 0; r <= rows; r++) {
    const row: [number, number][] = []
    for (let c = 0; c <= cols; c++) row.push(point(c, r) as [number, number])
    grid.push(row)
  }

  const polys: string[] = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const tl = grid[r][c]
      const tr = grid[r][c + 1]
      const bl = grid[r + 1][c]
      const br = grid[r + 1][c + 1]
      const opacityA = (rand() < openRate ? rand() * 0.3 + 0.45 : rand() * 0.12 + 0.06).toFixed(2)
      const opacityB = (rand() < openRate ? rand() * 0.3 + 0.45 : rand() * 0.12 + 0.06).toFixed(2)
      polys.push(`<polygon points='${tl[0].toFixed(1)},${tl[1].toFixed(1)} ${tr[0].toFixed(1)},${tr[1].toFixed(1)} ${bl[0].toFixed(1)},${bl[1].toFixed(1)}' fill='white' fill-opacity='${opacityA}'/>`)
      polys.push(`<polygon points='${tr[0].toFixed(1)},${tr[1].toFixed(1)} ${br[0].toFixed(1)},${br[1].toFixed(1)} ${bl[0].toFixed(1)},${bl[1].toFixed(1)}' fill='white' fill-opacity='${opacityB}'/>`)
    }
  }

  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${w} ${h}'><filter id='b'><feGaussianBlur stdDeviation='${blur}'/></filter><g filter='url(#b)'>${polys.join("")}</g></svg>`
  return svgDataUrl(svg)
}

/** "Confetti" pattern — same bimodal-open idea as the facet mesh but round
 * dots instead of triangles, for foils that want a scattered-glitter look
 * rather than crystal facets. */
export function buildDotsMesh(): string {
  const rand = mulberry32(9090)
  const dots: string[] = []
  for (let i = 0; i < 980; i++) {
    const x = rand() * 300
    const y = rand() * 450
    const r = 0.9 + rand() * 2.4
    const open = rand() > 0.24
    const opacity = open ? 0.42 + rand() * 0.5 : 0.05 + rand() * 0.14
    dots.push(`<circle cx='${x.toFixed(1)}' cy='${y.toFixed(1)}' r='${r.toFixed(2)}' fill='white' opacity='${opacity.toFixed(2)}'/>`)
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 450'><filter id='s'><feGaussianBlur stdDeviation='.25'/></filter><g filter='url(#s)'>${dots.join("")}</g></svg>`
  return svgDataUrl(svg)
}

/** A genuinely SUBTLE glisten — the facet/dots patterns are bimodal by
 * design ("either catches the light or it doesn't"), which reads as
 * graphic/geometric rather than delicate, and their specks are big enough
 * to look like confetti rather than a soft shimmer. This is tiny, heavily
 * blurred specks with a smooth (not on/off) intensity curve — mostly faint,
 * occasional brighter ones, closer to how light glints off a subtly
 * textured surface than a pattern with visible individual shapes. */
export function buildGlistenMesh(size = 1): string {
  const safeSize = size > 0 ? size : 1
  const rand = mulberry32(20260806)
  const marks: string[] = []
  for (let i = 0; i < 520; i++) {
    const x = rand() * 300
    const y = rand() * 450
    const r = (0.3 + rand() * 0.75) * safeSize
    // Cubing skews the distribution toward the low end so bright glints
    // stay rare and most specks stay faint — no hard "open or not" switch.
    const opacity = 0.03 + Math.pow(rand(), 3) * 0.82
    marks.push(`<circle cx='${x.toFixed(1)}' cy='${y.toFixed(1)}' r='${r.toFixed(2)}' fill='white' opacity='${opacity.toFixed(2)}'/>`)
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 450'><filter id='gl'><feGaussianBlur stdDeviation='${(0.6 * safeSize).toFixed(2)}'/></filter><g filter='url(#gl)'>${marks.join("")}</g></svg>`
  return svgDataUrl(svg)
}

function starPoints(cx: number, cy: number, outer: number, inner: number, points: number, rotate: number): string {
  const coords: string[] = []
  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outer : inner
    const angle = rotate + (i * Math.PI) / points
    coords.push(`${(cx + Math.cos(angle) * radius).toFixed(2)},${(cy + Math.sin(angle) * radius).toFixed(2)}`)
  }
  return coords.join(" ")
}

/** Starfield pattern — small dots plus occasional 4/5-point sparkle stars
 * with cross-flare lines, for the Starfield tier's "night sky" identity. */
export function buildStarsMesh(): string {
  const rand = mulberry32(13317)
  const marks: string[] = []
  for (let i = 0; i < 260; i++) {
    const x = rand() * 300
    const y = rand() * 450
    const big = rand() > 0.72
    const outer = big ? 2.4 + rand() * 4.6 : 0.75 + rand() * 1.55
    const inner = outer * (0.28 + rand() * 0.18)
    const opacity = big ? 0.42 + rand() * 0.48 : 0.12 + rand() * 0.42
    const rotate = rand() * Math.PI
    if (big) {
      marks.push(`<polygon points='${starPoints(x, y, outer, inner, 4 + Math.floor(rand() * 2), rotate)}' fill='white' opacity='${opacity.toFixed(2)}'/>`)
    } else {
      marks.push(`<circle cx='${x.toFixed(1)}' cy='${y.toFixed(1)}' r='${outer.toFixed(2)}' fill='white' opacity='${opacity.toFixed(2)}'/>`)
    }
    if (big && rand() > 0.48) {
      marks.push(`<path d='M${(x - outer * 1.8).toFixed(1)} ${y.toFixed(1)}H${(x + outer * 1.8).toFixed(1)}M${x.toFixed(1)} ${(y - outer * 1.8).toFixed(1)}V${(y + outer * 1.8).toFixed(1)}' stroke='white' stroke-width='.55' stroke-linecap='round' opacity='${(opacity * 0.72).toFixed(2)}'/>`)
    }
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 450'><filter id='g'><feGaussianBlur stdDeviation='.22'/></filter><g filter='url(#g)'>${marks.join("")}</g></svg>`
  return svgDataUrl(svg)
}

/** Rain-on-card pattern — rounded droplets plus a few vertical streaks, with
 * mild blur/displacement so it reads like water sitting on the card surface
 * rather than flat confetti. */
export function buildRainMesh(): string {
  const rand = mulberry32(20260804)
  const marks: string[] = []
  for (let i = 0; i < 170; i++) {
    const x = rand() * 300
    const y = rand() * 450
    const rx = 0.9 + rand() * 2.4
    const ry = rx * (1.4 + rand() * 2.2)
    const opacity = 0.12 + rand() * 0.58
    const rotate = -12 + rand() * 24
    marks.push(`<ellipse cx='${x.toFixed(1)}' cy='${y.toFixed(1)}' rx='${rx.toFixed(2)}' ry='${ry.toFixed(2)}' transform='rotate(${rotate.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)})' fill='white' opacity='${opacity.toFixed(2)}'/>`)
    if (rand() > 0.62) {
      const len = 8 + rand() * 28
      marks.push(`<path d='M${x.toFixed(1)} ${y.toFixed(1)}c${(rand() * 2 - 1).toFixed(1)} ${(len * 0.35).toFixed(1)} ${(rand() * 2 - 1).toFixed(1)} ${(len * 0.72).toFixed(1)} ${(rand() * 2 - 1).toFixed(1)} ${len.toFixed(1)}' stroke='white' stroke-width='${(0.45 + rand() * 1.1).toFixed(2)}' stroke-linecap='round' opacity='${(opacity * 0.72).toFixed(2)}'/>`)
    }
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 450'><filter id='r'><feTurbulence type='fractalNoise' baseFrequency='.018 .08' numOctaves='2' seed='14' result='n'/><feDisplacementMap in='SourceGraphic' in2='n' scale='1.6'/><feGaussianBlur stdDeviation='.38'/></filter><g filter='url(#r)'>${marks.join("")}</g></svg>`
  return svgDataUrl(svg)
}

/** Digital-glitch pattern — the "glitch" tier used plain grain before, which
 * doesn't read as glitch at all. This is torn horizontal scanline bands
 * (some shifted sideways off one edge, like a corrupted video frame),
 * blocky compression-artifact chunks, and a few thin bright CRT scan lines. */
export function buildGlitchMesh(): string {
  const rand = mulberry32(20260805)
  const marks: string[] = []

  // Torn scanline bands — most are dim "background" rows; ~1 in 5 is a
  // bright glitched row shifted off one edge, leaving a gap on the other
  // side (the classic "row got displaced sideways" look).
  for (let i = 0; i < 46; i++) {
    const y = rand() * 450
    const h = 1.5 + rand() * 8
    const glitched = rand() > 0.78
    if (glitched) {
      const shift = (rand() > 0.5 ? 1 : -1) * (14 + rand() * 46)
      const w = 300 - Math.abs(shift) * (0.6 + rand() * 0.5)
      const x = shift > 0 ? shift : 0
      marks.push(`<rect x='${x.toFixed(1)}' y='${y.toFixed(1)}' width='${Math.max(20, w).toFixed(1)}' height='${h.toFixed(1)}' fill='white' opacity='${(0.5 + rand() * 0.42).toFixed(2)}'/>`)
    } else {
      marks.push(`<rect x='0' y='${y.toFixed(1)}' width='300' height='${h.toFixed(1)}' fill='white' opacity='${(0.04 + rand() * 0.14).toFixed(2)}'/>`)
    }
  }

  // Blocky corrupted-pixel chunks, scattered irregular sizes.
  for (let i = 0; i < 60; i++) {
    const x = rand() * 300
    const y = rand() * 450
    const w = 6 + rand() * 34
    const h = 3 + rand() * 14
    marks.push(`<rect x='${x.toFixed(1)}' y='${y.toFixed(1)}' width='${w.toFixed(1)}' height='${h.toFixed(1)}' fill='white' opacity='${(0.1 + rand() * 0.5).toFixed(2)}'/>`)
  }

  // Sparse, thin, high-contrast full-width scan lines on top.
  for (let i = 0; i < 8; i++) {
    const y = rand() * 450
    marks.push(`<rect x='0' y='${y.toFixed(1)}' width='300' height='${(0.8 + rand() * 1.2).toFixed(1)}' fill='white' opacity='${(0.55 + rand() * 0.35).toFixed(2)}'/>`)
  }

  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 450'>${marks.join("")}</svg>`
  return svgDataUrl(svg)
}

/** Sparse field of soft, feathered circles — used as the "sparkle scatter"
 * mask shape (panned around independently of the card's own pattern mesh,
 * see maskShapeStyle below), not as a pattern in its own right. Blurred
 * heavily so each dot reads as a soft glint rather than a hard confetti
 * spot. */
export function buildSparkleMaskMesh(): string {
  const rand = mulberry32(20260810)
  const marks: string[] = []
  for (let i = 0; i < 70; i++) {
    const x = rand() * 300
    const y = rand() * 450
    const r = 6 + rand() * 22
    const opacity = 0.32 + rand() * 0.55
    marks.push(`<circle cx='${x.toFixed(1)}' cy='${y.toFixed(1)}' r='${r.toFixed(1)}' fill='white' opacity='${opacity.toFixed(2)}'/>`)
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 450'><filter id='sp'><feGaussianBlur stdDeviation='7'/></filter><g filter='url(#sp)'>${marks.join("")}</g></svg>`
  return svgDataUrl(svg)
}

export const SPARKLE_MASK_URL = buildSparkleMaskMesh()

/** CSS mask-image/position/size for a mask "shape" — the second, independent
 * control (alongside layerEffectRecipe) that constrains WHERE a layer
 * effect shows through rather than WHAT it looks like. `posX`/`posY` are
 * 0-100 (the shape's moving center — either the opposite-of-cursor point or
 * a randomized point, computed at the call site), `angleDeg` drives the
 * two directional band shapes. `sizeScale` (default 1) scales how big the
 * window is — e.g. how wide the radial spotlight or glint band is — this is
 * the size/strength knob admins can tune per preset. Radial/glint/glare are
 * expressed as plain CSS gradients (no SVG needed, positioned inline in the
 * gradient itself); sparkle reuses SPARKLE_MASK_URL panned via
 * mask-position, same technique as the pattern meshes above. */
export function maskShapeStyle(shape: Exclude<FoilMaskShape, "none">, posX: number, posY: number, angleDeg: number, sizeScale: number = 1): CSSProperties {
  let image = "none"
  let size = "100% 100%"
  let position = "0 0"
  const s = Math.max(0.1, sizeScale)

  if (shape === "radial") {
    // Kept moderate by default on purpose — too wide and the spotlight
    // covers nearly the whole card regardless of where it's centered,
    // which makes "mirror" vs "reverse mirror" look identical since almost
    // everything is inside the window either way. Too narrow and it
    // double-crops an effect that already has its own soft falloff
    // (glassSweep, specular). sizeScale lets each preset dial this instead
    // of it being a single fixed guess.
    const core = (20 * s).toFixed(1)
    const edgeStop = (46 * s).toFixed(1)
    image = `radial-gradient(circle at ${posX.toFixed(1)}% ${posY.toFixed(1)}%, white 0%, white ${core}%, transparent ${edgeStop}%)`
  } else if (shape === "glintThin" || shape === "glintThick") {
    const half = (shape === "glintThin" ? 6 : 13) * s
    const t = posX
    image = `linear-gradient(${angleDeg.toFixed(1)}deg, transparent ${Math.max(0, t - half - 12).toFixed(1)}%, transparent ${Math.max(0, t - half).toFixed(1)}%, white ${t.toFixed(1)}%, transparent ${Math.min(100, t + half).toFixed(1)}%, transparent ${Math.min(100, t + half + 12).toFixed(1)}%)`
  } else if (shape === "glareSweep") {
    const t = posX
    const inner = 14 * s
    const outer = 34 * s
    image = `linear-gradient(${angleDeg.toFixed(1)}deg, transparent ${Math.max(0, t - outer).toFixed(1)}%, rgba(255,255,255,.4) ${Math.max(0, t - inner).toFixed(1)}%, white ${t.toFixed(1)}%, rgba(255,255,255,.4) ${Math.min(100, t + inner).toFixed(1)}%, transparent ${Math.min(100, t + outer).toFixed(1)}%)`
  } else if (shape === "sparkle") {
    image = SPARKLE_MASK_URL
    size = `${(170 * s).toFixed(0)}% ${(170 * s).toFixed(0)}%`
    position = `${posX.toFixed(1)}% ${posY.toFixed(1)}%`
  }

  return {
    WebkitMaskImage: image,
    maskImage: image,
    WebkitMaskSize: size,
    maskSize: size,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: position,
    maskPosition: position,
  } as CSSProperties
}

// feTurbulence randomizes each of R/G/B independently by default, so without
// this the "grain" ends up as blotchy colored static (and can easily read as
// tinted — e.g. green — depending on the random seed) rather than the flat
// monochrome per-pixel look of a real film-grain/Photoshop-noise filter.
// Averaging the three channels together (a standard "grayscale via matrix"
// trick) correlates them so every pixel's R/G/B come out equal, before any
// contrast/gamma boosting is layered on top.
const GRAYSCALE_MATRIX = "0.333 0.333 0.333 0 0 0.333 0.333 0.333 0 0 0.333 0.333 0.333 0 0 0 0 0 1 0"

/** `size` is the admin-tunable grainSize multiplier (1 = shipped default) —
 * divides into baseFrequency so a bigger size value means a LOWER frequency,
 * i.e. bigger/chunkier specks, and a smaller size value means finer grain. */
export function buildGrainTexture(type: GrainType = "uniform", size = 1): string {
  const safeSize = size > 0 ? size : 1
  const freq = (type === "gaussian" ? 0.4 : 0.6) / safeSize
  // baseFrequency lower = bigger, more individually-visible specks (real
  // Photoshop "Add Noise" reads as distinct flecks, not a hazy blur); the
  // gamma/contrast pass after grayscale-conversion punches up per-speck
  // contrast so it doesn't just look like flat gray fog at typical card size.
  const filter =
    type === "gaussian"
      ? `<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='${freq}' numOctaves='4' seed='43' stitchTiles='stitch' result='n'/><feColorMatrix in='n' type='matrix' values='${GRAYSCALE_MATRIX}' result='g'/><feGaussianBlur in='g' stdDeviation='.34' result='b'/><feComponentTransfer><feFuncR type='gamma' amplitude='1.6' exponent='1.7' offset='-.2'/><feFuncG type='gamma' amplitude='1.6' exponent='1.7' offset='-.2'/><feFuncB type='gamma' amplitude='1.6' exponent='1.7' offset='-.2'/></feComponentTransfer></filter>`
      : `<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='${freq}' numOctaves='2' seed='31' stitchTiles='stitch' result='n'/><feColorMatrix in='n' type='matrix' values='${GRAYSCALE_MATRIX}' result='g'/><feComponentTransfer><feFuncR type='gamma' amplitude='1.6' exponent='1.6' offset='-.18'/><feFuncG type='gamma' amplitude='1.6' exponent='1.6' offset='-.18'/><feFuncB type='gamma' amplitude='1.6' exponent='1.6' offset='-.18'/></feComponentTransfer></filter>`
  const svg = `<svg xmlns='http://www.w3.org/2000/svg'>${filter}<rect width='100%' height='100%' filter='url(#n)' opacity='${type === "gaussian" ? ".68" : ".6"}'/></svg>`
  return svgDataUrl(svg)
}

/** A denser grain mask for selecting foil color, separate from the subtle
 * overlay grain texture. This gives admins a "grain as the actual pattern"
 * option instead of only using grain as a post texture. Same `size` idea as
 * buildGrainTexture above. */
export function buildGrainMesh(type: GrainType = "uniform", size = 1): string {
  const safeSize = size > 0 ? size : 1
  const freq = (type === "gaussian" ? 0.46 : 0.78) / safeSize
  const filter =
    type === "gaussian"
      ? `<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='${freq}' numOctaves='5' seed='37' stitchTiles='stitch' result='n'/><feColorMatrix in='n' type='matrix' values='${GRAYSCALE_MATRIX}' result='g'/><feGaussianBlur in='g' stdDeviation='.42' result='b'/><feColorMatrix in='b' type='matrix' values='2.25 0 0 0 -.55 0 2.25 0 0 -.55 0 0 2.25 0 -.55 0 0 0 1 0'/></filter>`
      : `<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='${freq}' numOctaves='3' seed='31' stitchTiles='stitch' result='n'/><feColorMatrix in='n' type='matrix' values='${GRAYSCALE_MATRIX}' result='g'/><feColorMatrix in='g' type='matrix' values='1.8 0 0 0 -.35 0 1.8 0 0 -.35 0 0 1.8 0 -.35 0 0 0 1 0'/></filter>`
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 450'>${filter}<rect width='300' height='450' filter='url(#n)' opacity='${type === "gaussian" ? ".82" : ".74"}'/></svg>`
  return svgDataUrl(svg)
}

// Computed once at module load, shared by every card/preset — only the
// color layers underneath change per foil, the pattern geometry doesn't.
export const FACET_MESH_URL = buildFacetMesh()
export const FACET_MESH_2_URL = buildFacetMesh(20260805, 18, 28, 0.58, 0.28, 1.25)
export const FINE_FACET_MESH_URL = buildFacetMesh(20260806, 34, 48, 0.34, 0.18, 0.65)
export const DOTS_MESH_URL = buildDotsMesh()
export const STARS_MESH_URL = buildStarsMesh()
export const RAIN_MESH_URL = buildRainMesh()
export const GLITCH_MESH_URL = buildGlitchMesh()
export const GLISTEN_MESH_URL = buildGlistenMesh()
export const UNIFORM_GRAIN_MESH_URL = buildGrainMesh("uniform")
export const GAUSSIAN_GRAIN_MESH_URL = buildGrainMesh("gaussian")
export const UNIFORM_GRAIN_TEXTURE_URL = buildGrainTexture("uniform")
export const GAUSSIAN_GRAIN_TEXTURE_URL = buildGrainTexture("gaussian")
export const GRAIN_MESH_URL = UNIFORM_GRAIN_MESH_URL
export const GRAIN_TEXTURE_URL = UNIFORM_GRAIN_TEXTURE_URL

// size !== 1 means an admin has actually tuned grainSize away from default —
// build it fresh rather than serving the precomputed size=1 constant.
export function grainTextureUrl(type: GrainType = "uniform", size = 1): string {
  if (size !== 1) return buildGrainTexture(type, size)
  return type === "gaussian" ? GAUSSIAN_GRAIN_TEXTURE_URL : UNIFORM_GRAIN_TEXTURE_URL
}

export function patternUrl(pattern: FoilPatternType, grainType: GrainType = "uniform", grainSize = 1, glistenSize = 1): string | null {
  if (pattern === "facet") return FACET_MESH_URL
  if (pattern === "facet2") return FACET_MESH_2_URL
  if (pattern === "fineFacet") return FINE_FACET_MESH_URL
  if (pattern === "dots") return DOTS_MESH_URL
  if (pattern === "stars") return STARS_MESH_URL
  if (pattern === "grain") {
    if (grainSize !== 1) return buildGrainMesh(grainType, grainSize)
    return grainType === "gaussian" ? GAUSSIAN_GRAIN_MESH_URL : UNIFORM_GRAIN_MESH_URL
  }
  if (pattern === "rain") return RAIN_MESH_URL
  if (pattern === "glitch") return GLITCH_MESH_URL
  if (pattern === "glisten") {
    if (glistenSize !== 1) return buildGlistenMesh(glistenSize)
    return GLISTEN_MESH_URL
  }
  return null
}

// ---------------------------------------------------------------------------
// Layout frame masks — real vector card-frame templates (border rings,
// corner brackets, plaques, notched corners, info panels) for the "layout"
// layer effect, replacing the old plain CSS padding-ring (which never
// rendered a cleanly rounded inner corner and just read as an unlabeled
// colored blob). Built in a fixed 200x300 (2:3) viewBox — the mask stretches
// to fill whatever size the actual card renders at, same technique as the
// facet meshes above.
//
// Corner radius is NOT a fixed constant here — it's passed in as
// `cornerPercent` (radius as % of card width) so it can be matched to
// whatever `border-radius` the actual card element is using. A card's own
// rounding is a fixed px value (e.g. rounded-2xl = 16px) applied to a
// variably-sized element, so there's no single percentage that's "correct"
// everywhere the card renders — cornerPercent lets each call site pass its
// own best estimate instead of every layout being baked to one guess.
const LAYOUT_W = 200
const LAYOUT_H = 300
export const DEFAULT_LAYOUT_CORNER_PERCENT = 6.5

function roundedRectPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2))
  return `M${x + rr},${y} H${x + w - rr} A${rr},${rr} 0 0 1 ${x + w},${y + rr} V${y + h - rr} A${rr},${rr} 0 0 1 ${x + w - rr},${y + h} H${x + rr} A${rr},${rr} 0 0 1 ${x},${y + h - rr} V${y + rr} A${rr},${rr} 0 0 1 ${x + rr},${y} Z`
}

// Rounded on the bottom two corners only, square on top — for the info-panel
// layout, which sits flush against the top of its own area rather than
// having a free-floating rounded top edge.
function bottomRoundedRectPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h))
  return `M${x},${y} H${x + w} V${y + h - rr} A${rr},${rr} 0 0 1 ${x + w - rr},${y + h} H${x + rr} A${rr},${rr} 0 0 1 ${x},${y + h - rr} Z`
}

// Rounded on the top two corners only, square on bottom — for a nameplate
// bar sitting flush at the very top of the card.
function topRoundedRectPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h))
  return `M${x},${y + h} V${y + rr} A${rr},${rr} 0 0 1 ${x + rr},${y} H${x + w - rr} A${rr},${rr} 0 0 1 ${x + w},${y + rr} V${y + h} Z`
}

// Ring = outer rounded-rect minus inner rounded-rect, combined into one path
// and filled with fill-rule=evenodd — both edges of the ring come out
// properly rounded (unlike the old CSS content-box/border-box mask hack),
// and both track the same `r` the card's own corner uses.
function ringPath(r: number, margin: number, thickness: number): string {
  // Was `r - margin` (and `r - inMargin` below) — a naive parallel-curve
  // inset that only looks right when r is comfortably bigger than the
  // margin. Here margin (8-16) is close to r itself (~11 at this card's
  // real proportions), so it collapsed to a near-zero radius — square
  // corners on a ring meant to match the card's actually-rounded ones.
  // Using r directly (roundedRectPath already clamps to w/2,h/2) keeps the
  // ring's corners bending at roughly the same radius as the card itself.
  const outer = roundedRectPath(margin, margin, LAYOUT_W - margin * 2, LAYOUT_H - margin * 2, r)
  const inMargin = margin + thickness
  const inner = roundedRectPath(inMargin, inMargin, LAYOUT_W - inMargin * 2, LAYOUT_H - inMargin * 2, r)
  return `${outer} ${inner}`
}

function octagonPath(x: number, y: number, w: number, h: number, cut: number): string {
  return `M${x + cut},${y} L${x + w - cut},${y} L${x + w},${y + cut} L${x + w},${y + h - cut} L${x + w - cut},${y + h} L${x + cut},${y + h} L${x},${y + h - cut} L${x},${y + cut} Z`
}

function diamondMark(cx: number, cy: number, size: number): string {
  return `<polygon points='${cx},${cy - size} ${cx + size},${cy} ${cx},${cy + size} ${cx - size},${cy}' fill='white'/>`
}

// Two arm rects (rounded stadium ends) plus a small rounded square filling
// the inner corner where they meet — without the filler, the rounded ends
// leave a visible gap right at the joint instead of reading as one bent bar.
function bracket(x: number, y: number, armLen: number, thick: number, dirX: 1 | -1, dirY: 1 | -1): string {
  const hx = dirX === 1 ? x : x - armLen
  const hy = dirY === 1 ? y : y - thick
  const vx = dirX === 1 ? x : x - thick
  const vy = dirY === 1 ? y : y - armLen
  const jointX = dirX === 1 ? x : x - thick
  const jointY = dirY === 1 ? y : y - thick
  return `<rect x='${hx}' y='${hy}' width='${armLen}' height='${thick}' rx='${thick / 2}' fill='white'/><rect x='${vx}' y='${vy}' width='${thick}' height='${armLen}' rx='${thick / 2}' fill='white'/><rect x='${jointX}' y='${jointY}' width='${thick}' height='${thick}' rx='${(thick * 0.3).toFixed(2)}' fill='white'/>`
}

function layoutFrameBody(kind: Exclude<FoilLayoutPatternType, "none">, r: number): string {
  switch (kind) {
    case "classicRing":
      // Thin, clean single border that hugs the card's own corner radius —
      // the safe/default frame.
      return `<path fill='white' fill-rule='evenodd' d='${ringPath(r, 9, 8)}'/>`
    case "dualLine":
      // Two thin concentric rings close together — fancy trading-card
      // double border, not two separate frames.
      return `<path fill='white' fill-rule='evenodd' d='${ringPath(r, 8, 4)} ${ringPath(r, 16, 4)}'/>`
    case "brackets": {
      // Corner brackets only, no full sides — modern/minimal, holo tier.
      const armLen = 44
      const thick = 9
      return [
        bracket(18, 18, armLen, thick, 1, 1),
        bracket(LAYOUT_W - 18, 18, armLen, thick, -1, 1),
        bracket(18, LAYOUT_H - 18, armLen, thick, 1, -1),
        bracket(LAYOUT_W - 18, LAYOUT_H - 18, armLen, thick, -1, -1),
      ].join("")
    }
    case "plaque": {
      // Thin border ring plus a small bottom name/stat plaque — a light
      // touch of info area, not the full Pokemon-style panel.
      const plaqueH = 50
      const plaqueY = LAYOUT_H - 78
      return `<path fill='white' fill-rule='evenodd' d='${ringPath(r, 9, 7)}'/><rect x='26' y='${plaqueY}' width='${LAYOUT_W - 52}' height='${plaqueH}' rx='${Math.min(14, plaqueH / 2)}' fill='white' opacity='0.85'/>`
    }
    case "notched": {
      // Octagon (corner-cut) ring instead of rounded corners — mythic tier.
      const outer = octagonPath(10, 10, LAYOUT_W - 20, LAYOUT_H - 20, 20)
      const inner = octagonPath(25, 25, LAYOUT_W - 50, LAYOUT_H - 50, 13)
      return `<path fill='white' fill-rule='evenodd' d='${outer} ${inner}'/>`
    }
    case "infoPanel": {
      // Large bottom info area with a divider line, no border anywhere —
      // the "big Pokemon-card info box" option, for tiers/presets that want
      // a real info area instead of a decorative border.
      const panelH = LAYOUT_H * 0.36
      const panelY = LAYOUT_H - panelH
      const panel = `<path d='${bottomRoundedRectPath(0, panelY, LAYOUT_W, panelH, r)}' fill='white' opacity='0.82'/>`
      const divider = `<rect x='${(LAYOUT_W * 0.14).toFixed(1)}' y='${(panelY + 22).toFixed(1)}' width='${(LAYOUT_W * 0.72).toFixed(1)}' height='2.4' rx='1.2' fill='white'/>`
      return panel + divider
    }
    case "diamond": {
      // Minimal corner-only accents, no ring at all — for presets that want
      // "just a few little things" rather than a full frame.
      const inset = 22
      return [
        diamondMark(inset, inset, 9),
        diamondMark(LAYOUT_W - inset, inset, 9),
        diamondMark(inset, LAYOUT_H - inset, 9),
        diamondMark(LAYOUT_W - inset, LAYOUT_H - inset, 9),
      ].join("")
    }
    case "circuit": {
      // Thin ring with inward circuit-trace ticks — glitch tier.
      const ticks: string[] = []
      for (let i = 0; i < 4; i++) {
        const x = 34 + i * 38
        ticks.push(`<rect x='${x}' y='9' width='4' height='12' fill='white'/>`)
        ticks.push(`<rect x='${x}' y='${LAYOUT_H - 21}' width='4' height='12' fill='white'/>`)
      }
      for (let i = 0; i < 3; i++) {
        const y = 60 + i * 62
        ticks.push(`<rect x='9' y='${y}' width='12' height='4' fill='white'/>`)
        ticks.push(`<rect x='${LAYOUT_W - 21}' y='${y}' width='12' height='4' fill='white'/>`)
      }
      return `<path fill='white' fill-rule='evenodd' d='${ringPath(r, 9, 7)}'/>${ticks.join("")}`
    }
    case "arcTop": {
      // Thin ring plus a crest/badge arc at top center — starfield tier.
      const cx = LAYOUT_W / 2
      const badge = `<path d='M${cx - 34},34 Q${cx},4 ${cx + 34},34 L${cx + 26},54 Q${cx},44 ${cx - 26},54 Z' fill='white' opacity='0.9'/>`
      return `<path fill='white' fill-rule='evenodd' d='${ringPath(r, 9, 9)}'/>${badge}`
    }
    case "statRow": {
      // Top nameplate bar + a bottom 3-cell stat row — sized for exactly
      // the card's real fields: name up top, HP / ATK / Shield as the three
      // cells below (see goondeck-tab.tsx's DeckDisplayCard.stats, always
      // exactly 3 entries). No full ring — just the two real info areas.
      const topH = 34
      const topBar = `<path d='${topRoundedRectPath(0, 0, LAYOUT_W, topH, r)}' fill='white' opacity='0.85'/>`
      const rowH = 46
      const rowY = LAYOUT_H - rowH
      const rowBg = `<path d='${bottomRoundedRectPath(0, rowY, LAYOUT_W, rowH, r)}' fill='white' opacity='0.82'/>`
      const cellDivider = (x: number) => `<rect x='${(x - 1).toFixed(1)}' y='${(rowY + 6).toFixed(1)}' width='2' height='${(rowH - 12).toFixed(1)}' fill='white' opacity='0.55'/>`
      return topBar + rowBg + cellDivider(LAYOUT_W / 3) + cellDivider((LAYOUT_W * 2) / 3)
    }
    case "pokemonFull": {
      // The full spread: nameplate bar, a meta-line divider (type ·
      // attribute · range), a 3-cell HP/ATK/Shield stat row, and open space
      // below the row for ability text — every real field on the card gets
      // its own designated area, Pokemon-card style.
      const topH = 30
      const topBar = `<path d='${topRoundedRectPath(0, 0, LAYOUT_W, topH, r)}' fill='white' opacity='0.85'/>`
      const panelH = LAYOUT_H * 0.34
      const panelY = LAYOUT_H - panelH
      const panel = `<path d='${bottomRoundedRectPath(0, panelY, LAYOUT_W, panelH, r)}' fill='white' opacity='0.78'/>`
      const metaDivider = `<rect x='${(LAYOUT_W * 0.12).toFixed(1)}' y='${(panelY + 16).toFixed(1)}' width='${(LAYOUT_W * 0.76).toFixed(1)}' height='2' fill='white' opacity='0.55'/>`
      const rowY = panelY + 28
      const rowH = 40
      const cellDivider = (x: number) => `<rect x='${(x - 1).toFixed(1)}' y='${rowY.toFixed(1)}' width='2' height='${rowH}' fill='white' opacity='0.5'/>`
      return topBar + panel + metaDivider + cellDivider(LAYOUT_W / 3) + cellDivider((LAYOUT_W * 2) / 3)
    }
    case "sideRail": {
      // Vertical stat rail down the right edge — HP / ATK / Shield stacked
      // instead of in a row, plus a small nameplate tag top-left. Same 3
      // real fields, different placement for presets that don't want a
      // bottom-heavy layout.
      const railW = 40
      const railX = LAYOUT_W - railW
      const railY = 40
      const railH = LAYOUT_H - 80
      const rail = `<path d='${roundedRectPath(railX, railY, railW, railH, Math.min(r, railW / 2))}' fill='white' opacity='0.8'/>`
      const cellDivider = (y: number) => `<rect x='${(railX + 6).toFixed(1)}' y='${(y - 1).toFixed(1)}' width='${(railW - 12).toFixed(1)}' height='2' fill='white' opacity='0.55'/>`
      const tag = `<path d='${topRoundedRectPath(0, 0, 88, 28, Math.min(r, 14))}' fill='white' opacity='0.85'/>`
      return rail + cellDivider(railY + railH / 3) + cellDivider(railY + (railH * 2) / 3) + tag
    }
    default:
      return ""
  }
}

export function buildLayoutFrameMask(kind: Exclude<FoilLayoutPatternType, "none">, cornerPercent: number = DEFAULT_LAYOUT_CORNER_PERCENT): string {
  const r = (cornerPercent / 100) * LAYOUT_W
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${LAYOUT_W} ${LAYOUT_H}'>${layoutFrameBody(kind, r)}</svg>`
  return svgDataUrl(svg)
}

const layoutMaskCache = new Map<string, string>()

/** Returns the mask image for a layout template at a given corner-radius
 * percentage, or null for "none" (which either turns the layout effect
 * fully off, or — for old presets that never got a layoutPattern — falls
 * back to a plain ring at the call site). `cornerPercent` should match the
 * card's own border-radius as a % of its rendered width so the frame's
 * corners line up with the card's actual rounded corner. */
export function layoutMaskUrl(pattern: FoilLayoutPatternType, cornerPercent: number = DEFAULT_LAYOUT_CORNER_PERCENT): string | null {
  if (pattern === "none") return null
  const key = `${pattern}:${cornerPercent}`
  let url = layoutMaskCache.get(key)
  if (!url) {
    url = buildLayoutFrameMask(pattern, cornerPercent)
    layoutMaskCache.set(key, url)
  }
  return url
}

export interface LayerEffectRecipe {
  opacity: number
  background: string
  blend: CSSProperties["mixBlendMode"]
  filter: string
}

export function hexToRgbTriple(hex: string): string {
  const clean = hex.replace("#", "")
  const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean
  const num = parseInt(full.slice(0, 6), 16)
  if (Number.isNaN(num)) return "255,255,255"
  return `${(num >> 16) & 255},${(num >> 8) & 255},${num & 255}`
}

/** Optional extra overlay applied to a specific target (full card / border
 * ring / edge line) on top of the base pattern+color system — ported from
 * the holo-card-perspective prototype's per-layer effect recipes. `colorA`/
 * `colorB` come from the preset so e.g. "gold" reads gold and "oilSlick"
 * still favors the preset's own palette rather than a hardcoded one.
 * `glowColor` tints the "glow" effect specifically — defaults to white. */
export function layerEffectRecipe(effect: FoilLayerEffect, colorA: string, colorB: string, grainType: GrainType = "uniform", glowColor: string = "#ffffff"): LayerEffectRecipe {
  const iridescent = `linear-gradient(calc(var(--hp-shine-angle, 110deg) + 90deg), rgba(0,0,0,.75), transparent 18%, rgba(255,255,255,.86) 35%, transparent 56%, rgba(0,0,0,.62)), conic-gradient(from var(--hp-tilt-angle, 0deg) at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), rgba(0,255,255,.86), rgba(255,255,70,.76), rgba(255,40,220,.82), rgba(70,255,130,.76), rgba(0,255,255,.86))`
  const oil = `radial-gradient(circle at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), rgba(255,255,255,.32), transparent 22%), conic-gradient(from var(--hp-tilt-angle, 0deg) at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), rgba(0,0,0,.92), rgba(0,255,220,.9), rgba(18,15,45,.96), rgba(255,42,148,.9), rgba(0,0,0,.92))`
  const polarize = iridescent
  const gold = `radial-gradient(circle at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), rgba(255,255,255,.56), transparent 20%), linear-gradient(135deg, rgba(110,67,7,.9), rgba(255,226,86,.95), rgba(138,79,8,.9))`
  const edgeRainbow = `conic-gradient(from var(--hp-tilt-angle, 0deg) at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), rgba(0,255,255,.95), rgba(255,255,80,.9), rgba(255,38,210,.95), rgba(90,255,120,.9), rgba(0,255,255,.95))`
  const grain = grainTextureUrl(grainType)
  const stars = `${STARS_MESH_URL}, radial-gradient(circle at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), rgba(255,255,255,.74), transparent 24%)`
  const starRainbow = `${STARS_MESH_URL}, conic-gradient(from var(--hp-tilt-angle, 0deg) at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), ${colorA}, ${colorB}, ${colorA})`
  const rain = `${RAIN_MESH_URL}, radial-gradient(circle at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), rgba(255,255,255,.7), transparent 24%), linear-gradient(var(--hp-shine-angle, 110deg), transparent 20%, rgba(255,255,255,.4) 48%, transparent 72%)`
  // A bright glint at the point OPPOSITE the cursor/tilt (--hp-glare-*-inv,
  // set by the overlay as 100% minus the real glare position) plus a
  // diagonal band on the inverted shine angle — so as you tilt the card,
  // the sparkle moves away from your cursor instead of chasing it, which
  // reads as catching light from a totally different angle than you're
  // looking from.
  const glassSweep = `radial-gradient(circle at var(--hp-glare-x-inv, 50%) var(--hp-glare-y-inv, 50%), rgba(255,255,255,.95), rgba(255,255,255,.4) 14%, transparent 30%), linear-gradient(var(--hp-shine-angle-inv, 290deg), transparent 40%, rgba(255,255,255,.06) 47%, rgba(255,255,255,.9) 50%, rgba(255,255,255,.06) 53%, transparent 60%)`
  // Pure white, no hue at all — these brighten whatever's underneath
  // (screen blend) rather than tinting it toward a color like every recipe
  // above. Glow = broad, gentle, follows the real cursor position (not
  // inverted) like a light source washing over the surface. Specular = a
  // small, tight, intense hotspot — a shiny highlight point, not a wash.
  const glowRgb = hexToRgbTriple(glowColor)
  const glow = `radial-gradient(circle at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), rgba(${glowRgb},.98), rgba(${glowRgb},.62) 26%, rgba(${glowRgb},.22) 48%, transparent 68%)`
  const specular = `radial-gradient(circle at var(--hp-glare-x, 50%) var(--hp-glare-y, 50%), rgba(255,255,255,1), rgba(255,255,255,.72) 7%, transparent 18%)`

  const recipes: Record<FoilLayerEffect, LayerEffectRecipe> = {
    none: { opacity: 0, background: "none", blend: "screen", filter: "none" },
    iridescent: { opacity: 0.6, background: iridescent, blend: "screen", filter: "saturate(2.8) contrast(1.25) brightness(1.12)" },
    oilSlick: { opacity: 0.62, background: oil, blend: "hard-light", filter: "saturate(4) contrast(2.2) brightness(.82)" },
    polarization: { opacity: 0.58, background: polarize, blend: "overlay", filter: "saturate(1.4) contrast(2.2) brightness(.96)" },
    gold: { opacity: 0.55, background: gold, blend: "overlay", filter: "saturate(1.15) contrast(1.35)" },
    edgeRainbow: { opacity: 0.55, background: edgeRainbow, blend: "screen", filter: "saturate(3.4) contrast(2.4)" },
    grain: { opacity: 0.5, background: grain, blend: "overlay", filter: "contrast(1.45)" },
    stars: { opacity: 0.55, background: stars, blend: "screen", filter: "saturate(2.2) contrast(1.45) brightness(1.15)" },
    starRainbow: { opacity: 0.65, background: starRainbow, blend: "screen", filter: "saturate(3.6) contrast(2) brightness(1.3)" },
    rain: { opacity: 0.58, background: rain, blend: "screen", filter: "saturate(1.4) contrast(1.5) brightness(1.12)" },
    glassSweep: { opacity: 0.6, background: glassSweep, blend: "screen", filter: "saturate(1.2) contrast(1.2) brightness(1.08)" },
    glow: { opacity: 0.7, background: glow, blend: "screen", filter: "none" },
    specular: { opacity: 0.85, background: specular, blend: "screen", filter: "none" },
  }
  return recipes[effect] || recipes.none
}
